import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Anchor, Box, Inline } from '@atlaskit/primitives/compiled';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { CharlieTable } from '@atlassian/charlie-table/compiled';
import type { ColumnDef } from '@atlassian/charlie-table/tanstack-helpers';

import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';

import type { SpaceForFilter } from '../../hooks/useSpacesForFilter.ts';
import type { CurrentRange, PaginationInfo } from '../../hooks/useContentMetrics.ts';

import { ContentMetricsEmptyState } from './ContentMetricsEmptyState.tsx';
import { TablePaginationFooter } from './TablePaginationFooter.tsx';

const styles = cssMap({
	spaceLink: {
		color: token('color.text'),
		textDecoration: 'none',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: '0px',
		'&:hover': {
			color: token('color.text'),
			textDecoration: 'underline',
		},
		'&:visited': {
			color: token('color.text'),
		},
	},
	spaceCellContainer: {
		overflow: 'hidden',
		minWidth: '0px',
	},
	spaceCellTruncate: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	headerCell: {
		font: token('font.heading.xxsmall'),
	},
	rightAlignedCell: {
		display: 'flex',
		justifyContent: 'flex-end',
		width: '100%',
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: token('space.200'),
	},
	loadingContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.600'),
	},
});

const i18n = defineMessages({
	contentTitleHeader: {
		id: 'performance-insights.content-metrics.table.header.content-title.non-final',
		defaultMessage: 'Content title',
		description:
			'Column header in the Content Metrics table located in the first column for the Content title. Labels the column that shows the title of each content item (e.g. page name). Read-only. Translate as the heading for a list of content titles.',
	},
	loadTimeRatingHeader: {
		id: 'performance-insights.content-metrics.table.header.load-time-rating.non-final',
		defaultMessage: 'Load time rating',
		description:
			'Table column header in the Content Metrics table for Load time rating. Labels the column that shows a performance rating (Fast / Moderate / Slow) for how quickly the content loads; values appear as coloured lozenges. Read-only. Translate as the heading for that rating column.',
	},
	medianLoadTimeHeader: {
		id: 'performance-insights.content-metrics.table.header.median-load-time.non-final',
		defaultMessage: 'Median load time',
		description:
			'Table column header in the Content Metrics table for Median load time. Labels the column that shows the median time (in seconds) it takes to load the content; cell values are numbers followed by "s" (e.g. "3.2s"). Read-only. Translate as the heading for that duration column.',
	},
	viewsHeader: {
		id: 'performance-insights.content-metrics.table.header.views.non-final',
		defaultMessage: 'Views',
		description:
			'Table column header in the Content Metrics table for Views. Labels the column that shows how many times the content was viewed (a count). Read-only. Translate as the heading for the view-count column; keep short (single word or short phrase where natural).',
	},
	spaceHeader: {
		id: 'performance-insights.content-metrics.table.header.space.non-final',
		defaultMessage: 'Space',
		description:
			'Table column header in the Content Metrics table for Space. Labels the column that shows the Confluence space the content belongs to. Translate as the heading for the space name column; "Space" here means a Confluence space (container for pages).',
	},
	contentOwnerHeader: {
		id: 'performance-insights.content-metrics.table.header.content-owner.non-final',
		defaultMessage: 'Content owner',
		description:
			'Table column header in the Content Metrics table for Content owner. Labels the column that shows who is responsible for or owns the content (user or role). Translate as the heading for the owner column.',
	},
	medianLoadTimeSeconds: {
		id: 'performance-insights.content-metrics.table.cell.median-load-time-seconds.non-final',
		defaultMessage: '{value}s',
		description:
			'Location: table cell in the Median load time column. UI element: numeric duration. Purpose: shows load time in seconds. Placeholder: {value} is the numeric duration (e.g. 3.2). The "s" is the seconds unit; translate or keep as appropriate for your locale (e.g. "s", " sec", or full word for seconds).',
	},
});

/**
 * Represents a single content item with its performance metrics.
 * This interface mirrors the ContentPerformance GraphQL response type.
 */
export interface ContentMetricRow {
	id: string;
	content: {
		contentTitle: string;
		link: string;
		contentId: string;
	};
	loadTimeRating: LoadTimeRating;
	medianLoadTime: number;
	views: number;
	space: {
		id: string;
		name: string;
	};
	contentOwner: {
		id: string;
		displayName: string;
	};
	operations: Array<{
		operation: string;
		targetType: string;
	}>;
}

export enum LoadTimeRating {
	SLOW = 'SLOW',
	MODERATE = 'MODERATE',
	FAST = 'FAST',
}

const LOAD_TIME_RATING_APPEARANCE: Record<LoadTimeRating, 'success' | 'warning' | 'danger'> = {
	[LoadTimeRating.FAST]: 'success',
	[LoadTimeRating.MODERATE]: 'warning',
	[LoadTimeRating.SLOW]: 'danger',
};

export interface ContentMetricsTableProps {
	items: ContentMetricRow[];
	loading: boolean;
	error?: Error | null;
	hasActiveFilters: boolean;
	spaces?: SpaceForFilter[];
	paginationInfo?: PaginationInfo | null;
	currentRange?: CurrentRange | null;
	pageSize?: number;
	onNextPage?: () => void;
	onPreviousPage?: () => void;
	onPageSizeChange?: (size: number) => void;
}

export const ContentMetricsTable = ({
	items,
	loading,
	error,
	hasActiveFilters,
	spaces = [],
	paginationInfo,
	currentRange,
	pageSize,
	onNextPage,
	onPreviousPage,
	onPageSizeChange,
}: ContentMetricsTableProps) => {
	const { formatMessage } = useIntl();

	const spaceMap = useMemo(() => new Map(spaces.map((s) => [s.id, s])), [spaces]);

	// Always throw network/infrastructure errors regardless of filter state.
	// Only suppress errors when filters are active and the error is a GraphQL
	// data error (e.g. no matching results), not a transport-level failure.
	if (error) {
		const isGraphQLDataError =
			'graphQLErrors' in error &&
			(error as any).graphQLErrors?.length > 0 &&
			!('networkError' in error && (error as any).networkError);
		if (!hasActiveFilters || !isGraphQLDataError) {
			throw error;
		}
	}

	const columns = useMemo<ColumnDef<ContentMetricRow>[]>(
		() => [
			{
				id: 'contentTitle',
				accessorKey: 'content.contentTitle',
				header: formatMessage(i18n.contentTitleHeader),
				enableSorting: false,
				cell: ({ row }) => row.original.content.contentTitle,
				size: 212,
			},
			{
				id: 'loadTimeRating',
				accessorKey: 'loadTimeRating',
				header: formatMessage(i18n.loadTimeRatingHeader),
				enableSorting: false,
				cell: ({ row }) => (
					<Lozenge
						appearance={LOAD_TIME_RATING_APPEARANCE[row.original.loadTimeRating as LoadTimeRating]}
						isBold
					>
						{row.original.loadTimeRating}
					</Lozenge>
				),
				size: 124,
			},
			{
				id: 'medianLoadTime',
				accessorKey: 'medianLoadTime',
				header: formatMessage(i18n.medianLoadTimeHeader),
				enableSorting: false,
				cell: ({ row }) => {
					// Convert milliseconds to seconds with 1 decimal place
					const seconds = (row.original.medianLoadTime / 1000).toFixed(1);
					return (
						<Box xcss={styles.rightAlignedCell}>
							{formatMessage(i18n.medianLoadTimeSeconds, {
								value: seconds,
							})}
						</Box>
					);
				},
				size: 135,
			},
			{
				id: 'views',
				accessorKey: 'views',
				header: formatMessage(i18n.viewsHeader),
				enableSorting: false,
				cell: ({ row }) => <Box xcss={styles.rightAlignedCell}>{row.original.views}</Box>,
				size: 95,
			},
			{
				id: 'space',
				accessorKey: 'space.name',
				header: formatMessage(i18n.spaceHeader),
				enableSorting: false,
				cell: ({ row }) => {
					const { id: spaceId, name: spaceName } = row.original.space;
					const spaceData = spaceMap.get(spaceId);
					const spaceKey = spaceData?.key;
					const spaceUrl = spaceKey ? SPACE_OVERVIEW.toUrl({ spaceKey }) : undefined;

					return (
						<Inline
							alignBlock="center"
							space="space.050"
							shouldWrap={false}
							xcss={styles.spaceCellTruncate}
						>
							<SpaceIcon size="xsmall" src={spaceData?.iconPath} label="" />
							{spaceUrl ? (
								<Anchor href={spaceUrl} xcss={styles.spaceLink}>
									{spaceName}
								</Anchor>
							) : (
								<Box xcss={styles.spaceCellTruncate}>{spaceName}</Box>
							)}
						</Inline>
					);
				},
				size: 165,
			},
			{
				id: 'contentOwner',
				accessorKey: 'contentOwner.displayName',
				header: formatMessage(i18n.contentOwnerHeader),
				enableSorting: false,
				// TODO: Add correct content owner UI implementation
				cell: ({ row }) => row.original.contentOwner.displayName,
				size: 150,
			},
		],
		[formatMessage, spaceMap],
	);

	// When there's an error with active filters, treat as no results rather
	// than showing stale cached data from the previous successful query.
	const effectiveItems = error && hasActiveFilters ? [] : items;
	const hasItems = effectiveItems && effectiveItems.length > 0;
	let tableBody: React.ReactNode;

	if (loading) {
		tableBody = (
			<Box xcss={styles.loadingContainer}>
				<Spinner size="large" />
			</Box>
		);
	} else if (hasItems) {
		tableBody = (
			<CharlieTable<ContentMetricRow>
				columnDefs={columns}
				data={effectiveItems}
				headerCellXcss={styles.headerCell}
			/>
		);
	} else {
		tableBody = <ContentMetricsEmptyState columnDefs={columns} />;
	}

	// Show pagination when data is available.
	const showPagination =
		hasItems &&
		paginationInfo != null &&
		(paginationInfo.hasNextPage || paginationInfo.hasPreviousPage) &&
		currentRange != null &&
		pageSize != null &&
		onNextPage != null &&
		onPreviousPage != null &&
		onPageSizeChange != null;

	return (
		<Box xcss={styles.container}>
			{tableBody}
			{showPagination && (
				<TablePaginationFooter
					paginationInfo={paginationInfo}
					currentRange={currentRange}
					pageSize={pageSize}
					loading={loading}
					onNextPage={onNextPage}
					onPreviousPage={onPreviousPage}
					onPageSizeChange={onPageSizeChange}
				/>
			)}
		</Box>
	);
};
